import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

import {
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ChartTooltipContent,
} from "@/src/components/ui/chart";

import {
  CHART_CONTAINER_CLASS,
  CHART_MARGIN,
  getValueYAxisProps,
  X_AXIS_PROPS,
} from "./chartLayout";
import {
  getLastNonZeroKey,
  getValueAxisConfig,
  sumSeriesValues,
  toChartConfig,
  toCumulative,
} from "./charts.utils";
import { endRoundedBarShape } from "./endRoundedBarShape";
import useTimelineFormatters from "./useTimelineFormatters";

import type { JSX } from "react";

import type {
  ChartMode,
  ChartSeries,
  ChartValueUnit,
  TimelineDatum,
  TimelineGranularity,
} from "./charts.types";
import type { BarRadius } from "./endRoundedBarShape";

const TOP_RADIUS: BarRadius = [4, 4, 0, 0];

interface TimelineBarChartProps<TKey extends string> {
  dataList: readonly TimelineDatum<TKey>[];
  seriesList: readonly ChartSeries<TKey>[];
  mode: ChartMode;
  granularity: TimelineGranularity;
  unit?: ChartValueUnit;
}

function TimelineBarChart<TKey extends string>({
  dataList,
  seriesList,
  mode,
  granularity,
  unit,
}: TimelineBarChartProps<TKey>): JSX.Element {
  const { t, i18n } = useTranslation("editor-analytics");
  const lang = i18n.language;
  const config = useMemo(() => toChartConfig(seriesList), [seriesList]);
  const { rowList, valueAxis } = useMemo(() => {
    const keyList = seriesList.map((series) => series.key);
    const modeDataList =
      mode === "cumulative" ? toCumulative(dataList, keyList) : dataList;
    const maxTotal = Math.max(
      0,
      ...modeDataList.map((datum) => sumSeriesValues(datum.values, keyList)),
    );
    return {
      rowList: modeDataList.map((datum) => ({
        date: datum.date,
        ...datum.values,
        endSeriesKey: getLastNonZeroKey(datum.values, keyList),
      })),
      valueAxis: getValueAxisConfig(maxTotal, lang, unit),
    };
  }, [dataList, lang, mode, seriesList, unit]);
  const { formatTick, formatTooltipLabel } = useTimelineFormatters(granularity);
  const isStacked = seriesList.length > 1;

  return (
    <ChartContainer className={CHART_CONTAINER_CLASS} config={config}>
      <BarChart accessibilityLayer data={rowList} margin={CHART_MARGIN}>
        <CartesianGrid vertical={false} />
        <XAxis
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...X_AXIS_PROPS}
          dataKey="date"
          minTickGap={24}
          tickFormatter={formatTick}
        />
        <YAxis
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...getValueYAxisProps(valueAxis)}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              labelFormatter={formatTooltipLabel}
              totalLabel={isStacked ? t("total") : undefined}
              valueFormatter={valueAxis.formatValue}
            />
          }
        />
        {isStacked ? <ChartLegend /> : null}
        {seriesList.map((series) => (
          <Bar
            key={series.key}
            dataKey={series.key}
            fill={`var(--color-${series.key})`}
            shape={endRoundedBarShape(rowList, series.key, TOP_RADIUS)}
            stackId="timeline"
            stroke={isStacked ? "var(--card)" : undefined}
          />
        ))}
      </BarChart>
    </ChartContainer>
  );
}

export default TimelineBarChart;
